// Visitor tracking utility for analytics
import { collection, addDoc, doc, getDoc } from 'firebase/firestore';
import { db, auth } from '../firebase';

// Function to detect device type
const getDeviceType = () => {
  const ua = navigator.userAgent;
  if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua)) {
    return 'tablet';
  }
  if (/Mobile|Android|iP(hone|od)|IEMobile|BlackBerry|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(ua)) {
    return 'mobile';
  }
  return 'desktop';
};

// Function to get browser name
const getBrowserName = () => {
  const ua = navigator.userAgent;
  let browserName = 'Unknown';
  
  if (ua.includes('Firefox')) {
    browserName = 'Firefox';
  } else if (ua.includes('SamsungBrowser')) {
    browserName = 'Samsung Internet';
  } else if (ua.includes('Opera') || ua.includes('OPR')) {
    browserName = 'Opera';
  } else if (ua.includes('Trident')) {
    browserName = 'Internet Explorer';
  } else if (ua.includes('Edge')) {
    browserName = 'Edge (Legacy)';
  } else if (ua.includes('Edg')) {
    browserName = 'Edge';
  } else if (ua.includes('Chrome')) {
    browserName = 'Chrome';
  } else if (ua.includes('Safari')) {
    browserName = 'Safari';
  }
  
  return browserName;
};

// Function to get approximate location (country) from timezone
const getApproximateLocation = () => {
  try {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    // Map common timezones to countries/regions
    if (timezone.includes('Europe/Athens')) return 'Greece';
    if (timezone.includes('Europe')) return 'Europe';
    if (timezone.includes('America')) return 'Americas';
    if (timezone.includes('Asia')) return 'Asia';
    if (timezone.includes('Africa')) return 'Africa';
    if (timezone.includes('Australia') || timezone.includes('Pacific')) return 'Oceania';
    return timezone.split('/')[0]; // Return continent
  } catch {
    return 'Unknown';
  }
};

// Check if user is admin
const isUserAdmin = async (user) => {
  if (!user) return false;
  
  try {
    const adminEmail = 'giannisaliev@gmail.com';
    if (user.email === adminEmail) return true;
    
    const userDoc = await getDoc(doc(db, 'users', user.uid));
    return userDoc.exists() && userDoc.data().isAdmin === true;
  } catch (error) {
    console.error('Error checking admin status:', error);
    return false;
  }
};

// Log visitor activity
export const logVisit = async (pagePath) => {
  try {
    // Don't log admin pages to avoid cluttering analytics
    if (pagePath.startsWith('/admin')) {
      return;
    }

    // Get current authenticated user
    const currentUser = auth.currentUser;
    
    // Don't track admin users
    if (currentUser) {
      const isAdmin = await isUserAdmin(currentUser);
      if (isAdmin) {
        console.log('🚫 Admin visit not tracked:', pagePath);
        return;
      }
    }

    // Get or create session ID
    const sessionId = getSessionId();
    
    // Get session start time (first time this session was created)
    let sessionStartTime = sessionStorage.getItem('visitor_session_start');
    if (!sessionStartTime) {
      sessionStartTime = new Date().toISOString();
      sessionStorage.setItem('visitor_session_start', sessionStartTime);
    }
    
    // Calculate session duration in seconds
    const sessionDuration = Math.floor((new Date() - new Date(sessionStartTime)) / 1000);

    const visitData = {
      page: pagePath,
      timestamp: new Date(),
      sessionId: sessionId,
      sessionStartTime: new Date(sessionStartTime),
      sessionDuration: sessionDuration, // in seconds
      device: getDeviceType(),
      browser: getBrowserName(),
      location: getApproximateLocation(),
      userAgent: navigator.userAgent,
      screenResolution: `${window.screen.width}x${window.screen.height}`,
      referrer: document.referrer || 'Direct',
      // User information (if logged in)
      userId: currentUser?.uid || null,
      userEmail: currentUser?.email || null,
      userName: currentUser?.displayName || null,
      isAuthenticated: !!currentUser,
    };

    await addDoc(collection(db, 'visitorLogs'), visitData);
    console.log('📊 Visitor tracked:', pagePath, currentUser ? `(${currentUser.email})` : '(anonymous)');
  } catch (error) {
    // Silently fail - don't disrupt user experience
    console.error('Error logging visit:', error);
  }
};

// Log user interactions (button clicks, popup opens, etc.)
export const logInteraction = async (interactionType, details = {}) => {
  try {
    // Get current authenticated user
    const currentUser = auth.currentUser;
    
    // Don't track admin users
    if (currentUser) {
      const isAdmin = await isUserAdmin(currentUser);
      if (isAdmin) {
        return;
      }
    }

    const sessionId = getSessionId();

    const interactionData = {
      type: 'interaction',
      interactionType: interactionType, // e.g., 'button_click', 'popup_open', 'link_click'
      details: details, // e.g., { button: 'Website', competition: 'Greek Open 2025', url: '...' }
      timestamp: new Date(),
      sessionId: sessionId,
      page: window.location.pathname,
      device: getDeviceType(),
      browser: getBrowserName(),
      userId: currentUser?.uid || null,
      userEmail: currentUser?.email || null,
      userName: currentUser?.displayName || null,
      isAuthenticated: !!currentUser,
    };

    await addDoc(collection(db, 'visitorLogs'), interactionData);
    console.log('📊 Interaction tracked:', interactionType, details);
  } catch (error) {
    console.error('Error logging interaction:', error);
  }
};

// Generate a simple session ID (stored in sessionStorage)
export const getSessionId = () => {
  let sessionId = sessionStorage.getItem('visitor_session_id');
  if (!sessionId) {
    sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem('visitor_session_id', sessionId);
  }
  return sessionId;
};
